import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Autocomplete,
  Chip,
  IconButton,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import AddHomeWorkOutlinedIcon from '@mui/icons-material/AddHomeWorkOutlined';

const T = {
  green: '#0a6b3e',
  greenLt: '#e5f5ec',
  greenMd: '#0d8a50',
  greenSf: 'rgba(10,107,62,0.06)',
  ink: '#0f1a13',
  subtle: '#526b5c',
  muted: '#8eaa98',
  bg: '#f5f8f6',
  border: '#dce8e1',
  borderL: '#eaf1ec',
  white: '#ffffff',
  font: "'Inter','DM Sans','Plus Jakarta Sans',sans-serif",
};

const outlineLegendNoGap = {
  '& .MuiOutlinedInput-notchedOutline legend': {
    maxWidth: '0 !important',
    width: '0 !important',
    fontSize: 0,
    lineHeight: 0,
    padding: 0,
  },
  '& .MuiOutlinedInput-notchedOutline legend span': {
    padding: '0 !important',
    width: '0 !important',
    minWidth: '0 !important',
    display: 'block',
  },
};

const fieldSx = {
  ...outlineLegendNoGap,
  '& .MuiOutlinedInput-root': {
    fontFamily: T.font,
    fontSize: 13,
    borderRadius: 2,
    bgcolor: T.white,
    '& fieldset': { borderColor: T.borderL },
    '&:hover fieldset': { borderColor: T.border },
    '&.Mui-focused fieldset': { borderColor: T.green },
  },
  '& .MuiInputLabel-root': {
    fontFamily: T.font,
    fontSize: 13,
    color: T.subtle,
    '&.Mui-focused': { color: T.green },
  },
  '& .MuiOutlinedInput-input': { fontFamily: T.font, fontSize: 13 },
};

const sectionLabelSx = {
  fontFamily: T.font,
  fontSize: 11,
  fontWeight: 700,
  color: T.muted,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  mb: 1,
};

const autocompleteInputSx = {
  ...fieldSx,
  '& .MuiOutlinedInput-root': {
    ...fieldSx['& .MuiOutlinedInput-root'],
    minHeight: 48,
    alignItems: 'flex-start',
    py: 0.75,
  },
};

const AddPropertyDialog = ({ open, onClose, onAdd, availableProperties, sharedPropertyIds = [] }) => {
  const [projects, setProjects] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [loading, setLoading] = useState(false);

  const sharedSet = useMemo(
    () => new Set(Array.isArray(sharedPropertyIds) ? sharedPropertyIds.map(String) : []),
    [sharedPropertyIds]
  );

  const isAlreadyShared = (option) => {
    const id = option?.value ?? option?.id;
    return id != null && sharedSet.has(String(id));
  };

  useEffect(() => {
    if (!availableProperties) return;
    setLoading(true);
    let projectsData = availableProperties.projects || availableProperties;
    if (!Array.isArray(projectsData)) {
      projectsData = [];
    }
    setProjects(projectsData);
    setLoading(false);
  }, [availableProperties]);

  const handleAdd = async () => {
    if (selectedProjects.length === 0) return;
    try {
      const projectIds = selectedProjects.map((project) =>
        project?.value || project?.id || project
      );
      await onAdd(projectIds);
      handleClose();
    } catch (error) {
      console.error('Error adding properties:', error);
    }
  };

  const handleClose = () => {
    setSelectedProjects([]);
    onClose();
  };

  const optionKey = (option, index) =>
    String(option?.value ?? option?.id ?? option?.label ?? index);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: 520,
          borderRadius: 2,
          fontFamily: T.font,
          overflow: 'hidden',
          border: `1px solid ${T.borderL}`,
          boxShadow: '0 12px 40px rgba(15,26,19,0.12), 0 2px 6px rgba(15,26,19,0.06)',
          m: { xs: 2, sm: 3 },
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2.25,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '12px',
              bgcolor: T.greenLt,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <AddHomeWorkOutlinedIcon sx={{ fontSize: 22, color: T.green }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="h2"
              sx={{
                fontFamily: T.font,
                fontSize: 17,
                fontWeight: 700,
                color: T.ink,
                lineHeight: 1.3,
                letterSpacing: '-0.02em',
              }}
            >
              Add properties
            </Typography>
            <Typography
              sx={{
                fontFamily: T.font,
                fontSize: 13,
                color: T.subtle,
                mt: 0.25,
                lineHeight: 1.45,
              }}
            >
              Choose projects to suggest for this lead
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={handleClose}
          size="small"
          aria-label="Close"
          sx={{
            color: T.muted,
            mt: 0.25,
            flexShrink: 0,
            borderRadius: 2,
            '&:hover': { color: T.ink, bgcolor: T.greenSf },
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <Divider sx={{ borderColor: T.borderL }} />

      <DialogContent sx={{ px: 3, pt: 2, pb: 2.5 }}>
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4,
            }}
          >
            <CircularProgress size={28} thickness={4} sx={{ color: T.green }} />
          </Box>
        ) : (
          <Box sx={{ width: '100%' }}>
            <Typography sx={sectionLabelSx}>Projects</Typography>
            <Autocomplete
            multiple
            size="small"
            options={projects}
            getOptionKey={(option) => optionKey(option)}
            getOptionLabel={(option) => option?.label || ''}
            getOptionDisabled={isAlreadyShared}
            value={selectedProjects}
            onChange={(_, newValue) => setSelectedProjects(newValue)}
            noOptionsText={projects.length === 0 ? 'No projects available' : 'No matching projects'}
            slotProps={{
              paper: {
                sx: {
                  borderRadius: 2,
                  border: `1px solid ${T.borderL}`,
                  boxShadow: '0 10px 28px rgba(15,26,19,0.1)',
                  fontFamily: T.font,
                  mt: 0.5,
                  maxHeight: 320,
                },
              },
              listbox: {
                sx: {
                  maxHeight: 280,
                  py: 0.5,
                  '& li': { minHeight: 40 },
                },
              },
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={optionKey(option, index)}
                  label={option?.label || 'Project'}
                  size="small"
                  sx={{
                    fontFamily: T.font,
                    fontSize: 12,
                    fontWeight: 600,
                    bgcolor: T.greenLt,
                    color: T.green,
                    border: `1px solid ${T.borderL}`,
                    '& .MuiChip-deleteIcon': {
                      color: T.subtle,
                      fontSize: 16,
                      '&:hover': { color: T.ink },
                    },
                  }}
                />
              ))
            }
            renderOption={(props, option) => {
              const alreadyShared = isAlreadyShared(option);
              return (
                <Box
                  component="li"
                  {...props}
                  key={optionKey(option)}
                  sx={{
                    fontFamily: T.font,
                    fontSize: 13,
                    ...(alreadyShared && {
                      bgcolor: T.bg,
                      '&.Mui-focused': { bgcolor: T.bg },
                    }),
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <HomeWorkOutlinedIcon
                      sx={{
                        fontSize: 18,
                        color: alreadyShared ? T.muted : T.subtle,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      component="span"
                      sx={{
                        flex: 1,
                        fontFamily: T.font,
                        fontSize: 13,
                        color: alreadyShared ? T.muted : T.ink,
                      }}
                    >
                      {option?.label || ''}
                    </Typography>
                    {alreadyShared && (
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: T.font,
                          fontSize: 11,
                          color: T.muted,
                          fontStyle: 'italic',
                          flexShrink: 0,
                        }}
                      >
                        Already shared
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                hiddenLabel
                variant="outlined"
                placeholder={
                  projects.length === 0
                    ? 'Loading projects…'
                    : 'Search projects…'
                }
                fullWidth
                InputProps={{ ...params.InputProps, notched: false }}
                sx={autocompleteInputSx}
              />
            )}
          />
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 1.75,
          gap: 1,
          justifyContent: 'flex-end',
          alignItems: 'center',
          borderTop: `1px solid ${T.borderL}`,
          bgcolor: T.bg,
        }}
      >
        <Button
          onClick={handleClose}
          size="small"
          sx={{
            fontFamily: T.font,
            fontSize: 13,
            fontWeight: 500,
            color: T.subtle,
            textTransform: 'none',
            borderRadius: 2,
            px: 2,
            '&:hover': { bgcolor: T.greenSf, color: T.ink },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAdd}
          size="small"
          disabled={selectedProjects.length === 0}
          sx={{
            fontFamily: T.font,
            fontSize: 13,
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: 2,
            px: 2.5,
            minWidth: 100,
            height: 36,
            bgcolor: T.green,
            color: '#fff',
            boxShadow: 'none',
            '&:hover': { bgcolor: T.greenMd, boxShadow: 'none' },
            '&.Mui-disabled': { bgcolor: T.borderL, color: T.muted },
          }}
        >
          Add
          {selectedProjects.length > 0 ? ` (${selectedProjects.length})` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPropertyDialog;
